/**
 * Pictures for an article: one cover, and as many inline ones as it needs.
 *
 * The two are handled differently on purpose. A cover is a property of the
 * article, so generating one writes it and this shows what is there. An inline
 * picture's position is its markdown in the body, so this hands the writer a
 * block to place - dragged, or inserted at the caret, exactly like a CTA.
 *
 * The brief is subject matter, not instruction: the server assembles the prompt
 * around it and appends the prohibitions (no text, no before-and-after, no
 * procedures) after everything typed here, where nothing can displace them.
 */
import { useRef, useState } from 'react';
import { ApiError, del, imageUrl, post, postImage, putImage } from '../api.ts';
import { downscale } from './ImageUpload.tsx';
import { MARKDOWN_DRAG_MIME } from '../rich-text.ts';
import { ARTICLE_SIZES, DEFAULT_RATIO, OTHER_RATIOS } from '../../../shared/image-sizes.ts';
import { Select } from './Select.tsx';

/**
 * 「おまかせ」: no ratio is sent at all.
 *
 * The picture is drawn to whatever suits the subject, which is a real answer
 * and not the same as a default - it is the one option here that lets the model
 * make the call, and a writer who has not thought about shape is better served
 * by it than by a square they did not choose.
 */
const AUTO = 'auto';

/**
 * An uploaded picture, as sent: 1600px on the long edge covers a full-width
 * article column on a high-density screen, and keeps a phone photograph far
 * under the server's 1 MB (MAX_ARTICLE_IMAGE_BYTES in server/lib/image.ts).
 */
const UPLOAD_EDGE = 1600;
const UPLOAD_MAX_BYTES = 1024 * 1024;

/** What the server said, in words a writer can act on. */
function uploadError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 413 || /KB or smaller/.test(err.message)) return '画像が大きすぎます（1MBまで）。';
    if (err.status === 415 || /PNG and JPEG|not the type/.test(err.message))
      return 'PNG または JPEG の画像を選んでください。';
    return err.message;
  }
  return '画像を読み込めませんでした。PNG または JPEG の画像を選んでください。';
}

interface Generated {
  id: string;
  slot: 'cover' | 'inline';
  url: string;
  provider: string;
  microCost: number;
  /** Carried so a picture drawn for a section can be inserted into it. */
  heading?: string;
  ratio?: string;
}

export function ArticleImages({
  articleId,
  coverImageId,
  headings,
  onInsert,
  onInsertUnderHeading,
  onCoverChange,
}: {
  articleId: string;
  coverImageId: string | null;
  /** Offered as the section a supporting picture belongs beside. */
  headings: string[];
  /** Insert markdown at the caret. The editor owns the body text. */
  onInsert: (markdown: string) => void;
  /**
   * Insert into the section the picture was drawn for.
   *
   * The panel knows which heading it asked for; the caret does not. Used
   * whenever a picture has a heading, with the caret as the fallback.
   */
  onInsertUnderHeading: (markdown: string, heading: string) => void;
  /** The cover was written server-side; the screen has to catch up. */
  onCoverChange: () => void;
}) {
  const [brief, setBrief] = useState('');
  /*
   * The section this picture is for, preselected.
   *
   * It defaulted to 「指定しない」, and an inline picture drawn without a heading
   * reaches the model with the article's title and nothing else - which is the
   * generic-image complaint. Almost every inline picture belongs to a section,
   * so the first one is a better starting answer than none; 「指定しない」 is still
   * there for a picture that belongs wherever the caret is.
   */
  const [heading, setHeading] = useState(headings[0] ?? '');
  /*
   * The shape, per slot, because they are different decisions: a cover is a
   * banner and an inline picture sits in a column. Seeded from the defaults that
   * used to be hard-coded, so doing nothing here draws what it always drew.
   */
  const [coverRatio, setCoverRatio] = useState<string>(DEFAULT_RATIO.cover);
  const [inlineRatio, setInlineRatio] = useState<string>(DEFAULT_RATIO.inline);
  const [busy, setBusy] = useState<'cover' | 'inline' | 'upload-cover' | 'upload-inline' | null>(null);
  const coverFile = useRef<HTMLInputElement>(null);
  const inlineFile = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  /** Inline pictures drawn this sitting, waiting to be placed. */
  const [drawn, setDrawn] = useState<Generated[]>([]);

  async function draw(slot: 'cover' | 'inline') {
    setBusy(slot);
    setError(null);
    try {
      const ratio = slot === 'cover' ? coverRatio : inlineRatio;
      const result = await post<Generated>(`/articles/${articleId}/images`, {
        slot,
        // おまかせ says so outright rather than by omission: an absent ratio
        // already means "the slot's default" to every other caller.
        ...(ratio === AUTO ? { chooseShape: true } : { aspectRatio: ratio }),
        ...(brief.trim() ? { brief: brief.trim() } : {}),
        ...(slot === 'inline' && heading ? { heading } : {}),
      });
      if (slot === 'cover') {
        onCoverChange();
      } else {
        // Newest first: the one just drawn is the one being judged. The heading
        // it was drawn FOR travels with it, so inserting it later still knows
        // where it belongs even after the picker has moved on.
        setDrawn((current) => [{ ...result, heading, ratio }, ...current]);
      }
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.code === 'prompt_not_configured'
            ? '画像の作り方の指示文がまだ保存されていません。JMCにプロンプトの設定を依頼してください。'
            : err.status === 429
            ? '今月の画像生成の上限に達しています。'
            : (err.fields[0] ?? err.message)
          : '画像を生成できませんでした',
      );
    } finally {
      setBusy(null);
    }
  }

  /** A cover the writer brings. Replaces the one there, like 作り直す. */
  async function uploadCover(file: File) {
    setBusy('upload-cover');
    setError(null);
    try {
      await putImage(`/articles/${articleId}/cover`, await downscale(file, UPLOAD_EDGE, UPLOAD_MAX_BYTES));
      onCoverChange();
    } catch (err) {
      setError(uploadError(err));
    } finally {
      setBusy(null);
      if (coverFile.current) coverFile.current.value = '';
    }
  }

  /**
   * Pictures for the body. Several at once, each stored as it is read, and
   * each landing in the same strip a generated one does - so placing it is the
   * same drag or button, into the section chosen above.
   */
  async function uploadInline(files: File[]) {
    setBusy('upload-inline');
    setError(null);
    try {
      for (const file of files) {
        const blob = await downscale(file, UPLOAD_EDGE, UPLOAD_MAX_BYTES);
        const stored = await postImage(`/articles/${articleId}/uploads`, blob);
        setDrawn((current) => [
          { id: stored.id, slot: 'inline', url: stored.url, provider: 'upload', microCost: 0, heading },
          ...current,
        ]);
      }
    } catch (err) {
      setError(uploadError(err));
    } finally {
      setBusy(null);
      if (inlineFile.current) inlineFile.current.value = '';
    }
  }

  async function removeCover() {
    setError(null);
    try {
      await del(`/articles/${articleId}/cover`);
      onCoverChange();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'カバー画像を削除できませんでした');
    }
  }

  /** Alt text matters for a published article, so it is never left empty. */
  const markdownFor = (image: Generated) =>
    `![${(image.heading || brief).trim() || '記事内の画像'}](${image.url})`;

  /**
   * Put it where it belongs.
   *
   * A picture drawn for 「施術当日の流れ」 goes into that section; one drawn with
   * no heading goes to the caret, which is the only thing that knows where the
   * writer is looking.
   */
  const place = (image: Generated) => {
    const markdown = markdownFor(image);
    if (image.heading) onInsertUnderHeading(markdown, image.heading);
    else onInsert(markdown);
  };

  /**
   * The size control, shared by both slots.
   *
   * Named by where in the ARTICLE each shape belongs, because that is the only
   * place these pictures go. The list used to offer 「SNS向けの縦画像」, which is a
   * shape for a platform this product does not publish to - dropped into a
   * column of body text it is a picture the reader scrolls past.
   *
   * 「おまかせ」 first, then the four an article uses, then the rest of the
   * ratios the models accept - present but unadvertised, for a clinic whose own
   * template asks for one.
   */
  const sizeField = (id: string, value: string, onChange: (next: string) => void) => (
    <div className="field" style={{ marginBottom: 8 }}>
      <label htmlFor={id}>画像の形</label>
      <Select
        id={id}
        value={value}
        onChange={onChange}
        options={[
          { value: AUTO, label: 'おまかせ（内容に合わせてAIが決めます）' },
          ...ARTICLE_SIZES.map((size) => ({
            value: size.ratio,
            label: `${size.label}（${size.ratio}） ${size.hint}`,
          })),
          ...OTHER_RATIOS.map((ratio) => ({ value: ratio, label: `その他 ${ratio}` })),
        ]}
      />
    </div>
  );

  return (
    <>
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className="editpanel__body">
        <div className="field field--full">
          <label htmlFor="img-brief">どんな画像にするか（任意）</label>
          <input
            id="img-brief"
            maxLength={300}
            placeholder="例：鏡を見る女性の横顔、朝の光"
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
          />
          <span className="field__hint">
            記事のタイトル・施術・ペルソナは自動で伝わります。文字入り・施術中・ビフォーアフターの
            画像は生成しません。
          </span>
        </div>
      </div>

      <p className="form__section">カバー画像</p>
      {coverImageId ? (
        <div className="coverpreview">
          <img src={imageUrl(coverImageId)} alt="カバー画像" />
          <div className="editpanel__actions">
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              disabled={busy !== null}
              onClick={() => void draw('cover')}
            >
              {busy === 'cover' ? '生成中…' : '作り直す'}
            </button>
            <label className="btn btn--ghost btn--sm" htmlFor="img-cover-file" aria-disabled={busy !== null}>
              {busy === 'upload-cover' ? 'アップロード中…' : '画像を差し替え'}
            </label>
            <button className="btn btn--ghost btn--sm" type="button" onClick={() => void removeCover()}>
              削除
            </button>
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              onClick={() => onInsert(`![カバー画像](${imageUrl(coverImageId)})`)}
            >
              本文にも入れる
            </button>
          </div>
        </div>
      ) : (
        <>
          {sizeField('img-cover-ratio', coverRatio, setCoverRatio)}
          <div className="editpanel__actions">
            <button
              className="btn btn--sm"
              type="button"
              disabled={busy !== null}
              onClick={() => void draw('cover')}
            >
              {busy === 'cover' ? '生成中…' : 'カバー画像を生成'}
            </button>
            <label className="btn btn--ghost btn--sm" htmlFor="img-cover-file" aria-disabled={busy !== null}>
              {busy === 'upload-cover' ? 'アップロード中…' : '画像をアップロード'}
            </label>
            <span className="muted small">見出しを載せる余白を残して生成します。</span>
          </div>
        </>
      )}

      {/* One hidden picker per slot; the visible controls are their labels. */}
      <input
        ref={coverFile}
        id="img-cover-file"
        className="imgpick__input"
        type="file"
        accept="image/png,image/jpeg"
        disabled={busy !== null}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void uploadCover(file);
        }}
      />

      <p className="form__section">本文中の画像</p>
      <div className="field" style={{ marginBottom: 8 }}>
        <label htmlFor="img-heading">どの見出しの画像か</label>
        <Select
          id="img-heading"
          value={heading}
          onChange={setHeading}
          options={[
            { value: '', label: '指定しない（カーソル位置に入ります）' },
            ...headings.map((entry) => ({ value: entry, label: entry })),
          ]}
        />
        <span className="field__hint">
          選ぶと、その節の本文に入ります。画像はその節に書かれている内容をもとに生成されます。
        </span>
      </div>
      {sizeField('img-inline-ratio', inlineRatio, setInlineRatio)}
      <div className="editpanel__actions">
        <button
          className="btn btn--sm"
          type="button"
          disabled={busy !== null}
          onClick={() => void draw('inline')}
        >
          {busy === 'inline' ? '生成中…' : '本文用の画像を生成'}
        </button>
        <label className="btn btn--ghost btn--sm" htmlFor="img-inline-file" aria-disabled={busy !== null}>
          {busy === 'upload-inline' ? 'アップロード中…' : '画像をアップロード'}
        </label>
        <input
          ref={inlineFile}
          id="img-inline-file"
          className="imgpick__input"
          type="file"
          accept="image/png,image/jpeg"
          multiple
          disabled={busy !== null}
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length > 0) void uploadInline(files);
          }}
        />
        <span className="muted small">ドラッグでも置けます。</span>
      </div>
      <span className="field__hint">
        アップロードできるのは PNG・JPEG です。長辺1600pxに縮小され、位置情報などのデータは削除されます。
        写っている方の同意がある画像だけを使ってください。
      </span>

      {drawn.length > 0 && (
        <div className="imagestrip">
          {drawn.map((image) => (
            <figure
              key={image.id}
              className="imagestrip__item"
              draggable
              onDragStart={(event) => {
                event.dataTransfer.setData(MARKDOWN_DRAG_MIME, markdownFor(image));
                // Kept as well, so a drop into a plain textarea still lands.
                event.dataTransfer.setData('text/plain', markdownFor(image));
                event.dataTransfer.effectAllowed = 'copy';
              }}
            >
              <img src={image.url} alt={image.provider === 'upload' ? 'アップロードした画像' : '生成した画像'} />
              <figcaption>
                <button
                  className="btn btn--ghost btn--sm"
                  type="button"
                  onClick={() => place(image)}
                >
                  {image.heading ? 'この節に入れる' : '挿入'}
                </button>
                {/* What it cost, on the picture it paid for. A per-token image
                    model bills by pixels, so this is where a bigger shape shows
                    up - said once, beside the thing, rather than as a warning
                    beside the size control before anything has been drawn. */}
                {image.microCost > 0 && (
                  <span className="muted small">${(image.microCost / 1_000_000).toFixed(3)}</span>
                )}
                {image.provider === 'stub' && <span className="muted small">スタブ画像</span>}
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </>
  );
}
